import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  ShoppingCart,
  ShoppingBag,
  ArrowLeft,
  Trash2,
  Plus,
  Minus,
  Package,
  CreditCard,
  Truck,
  Shield,
  ArrowRight,
  Sparkles,
  Mail,
  Phone,
  MapPin,
  User,
  FileText,
  Building2,
  Search,
  Check,
  Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Debounce hook for autocomplete
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
}

export default function CartPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("card");
  
  // Company info state (for B2B invoice)
  const [companyInfo, setCompanyInfo] = useState({
    customer_type: "company",
    company_name: "",
    org_number: "",
    address: "",
    postal_code: "",
    city: "",
    contact_person: "",
    contact_email: "",
    contact_phone: ""
  });
  
  // Private person info state (for private invoice)
  const [privateInfo, setPrivateInfo] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    postal_code: "",
    city: ""
  });
  
  // Guest info state (for card payment)
  const [guestInfo, setGuestInfo] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    postal_code: ""
  });

  // Shipping options state
  const [shippingOptions, setShippingOptions] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [shippingLoading, setShippingLoading] = useState(false);

  // Lookup states
  const [companyLookupLoading, setCompanyLookupLoading] = useState(false);
  const [companyFound, setCompanyFound] = useState(false);
  const [postalLookupLoading, setPostalLookupLoading] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [addressQuery, setAddressQuery] = useState("");
  const addressInputRef = useRef(null);
  
  const debouncedAddressQuery = useDebounce(addressQuery, 300);

  useEffect(() => {
    loadCart();
  }, []);

  // Address autocomplete effect
  useEffect(() => {
    const fetchAddressSuggestions = async () => {
      if (debouncedAddressQuery.length < 3) {
        setAddressSuggestions([]);
        return;
      }
      
      try {
        const res = await axios.get(`${API}/lookup/address/autocomplete`, {
          params: { query: debouncedAddressQuery }
        });
        setAddressSuggestions(res.data.suggestions || []);
      } catch (err) {
        console.error("Address lookup error:", err);
      }
    };
    
    fetchAddressSuggestions();
  }, [debouncedAddressQuery]);

  const loadCart = () => {
    const savedCart = localStorage.getItem("allstillas_cart");
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  };

  const saveCart = (newCart) => {
    localStorage.setItem("allstillas_cart", JSON.stringify(newCart));
    setCart(newCart);
  };

  const updateQuantity = (index, delta) => {
    const newCart = [...cart];
    newCart[index].quantity += delta;
    
    if (newCart[index].quantity <= 0) {
      newCart.splice(index, 1);
    }
    
    saveCart(newCart);
  };

  const removeItem = (index) => {
    const newCart = [...cart];
    newCart.splice(index, 1);
    saveCart(newCart);
    toast.success("Product removed from cart");
  };

  const clearCart = () => {
    saveCart([]);
    toast.success("Cart has been cleared");
  };

  // Company lookup from Brønnøysund Register
  const lookupCompany = async (orgNumber) => {
    const cleanOrgNumber = orgNumber.replace(/\s/g, "").replace(/\./g, "");
    if (cleanOrgNumber.length !== 9) return;
    
    setCompanyLookupLoading(true);
    setCompanyFound(false);
    
    try {
      const res = await axios.get(`${API}/lookup/company/${cleanOrgNumber}`);
      if (res.data.found) {
        setCompanyInfo(prev => ({
          ...prev,
          company_name: res.data.name,
          address: res.data.address,
          postal_code: res.data.postal_code,
          city: res.data.city
        }));
        setCompanyFound(true);
        toast.success(`Company found: ${res.data.name}`);
      }
    } catch (err) {
      if (err.response?.status === 400 && err.response?.data?.detail?.includes("already registered")) {
        // Company exists but that's fine for checkout
        toast.info("Company found in our system");
      } else if (err.response?.status === 404) {
        toast.error("Company not found in Norwegian register");
      } else {
        console.error("Company lookup error:", err);
      }
    } finally {
      setCompanyLookupLoading(false);
    }
  };

  // Calculate shipping options based on postal code
  const calculateShipping = async (toPostal) => {
    const cleanPostal = toPostal.replace(/\s/g, "");
    if (cleanPostal.length !== 4) return;
    
    setShippingLoading(true);
    
    try {
      // Calculate total weight from cart products (use weight_kg if available, else estimate 0.5kg)
      const totalWeightKg = cart.reduce((sum, item) => {
        const itemWeight = item.weight_kg || 0.5; // Default 0.5kg if not specified
        return sum + (item.quantity * itemWeight);
      }, 0);
      
      // Calculate total dimensions (simplified: use largest item dimensions)
      let maxLength = 0, maxWidth = 0, maxHeight = 0;
      cart.forEach(item => {
        if (item.length_cm > maxLength) maxLength = item.length_cm || 0;
        if (item.width_cm > maxWidth) maxWidth = item.width_cm || 0;
        if (item.height_cm > maxHeight) maxHeight = item.height_cm || 0;
      });
      
      const res = await axios.get(`${API}/lookup/shipping/calculate`, {
        params: {
          to_postal: cleanPostal,
          weight_kg: Math.max(totalWeightKg, 1), // Minimum 1kg
          length_cm: maxLength,
          width_cm: maxWidth,
          height_cm: maxHeight
        }
      });
      
      if (res.data.options && res.data.options.length > 0) {
        setShippingOptions(res.data.options);
        // Auto-select first free option, or cheapest
        const freeOption = res.data.options.find(opt => opt.is_free);
        if (freeOption) {
          setSelectedShipping(freeOption);
        } else {
          const cheapest = res.data.options.reduce((min, opt) => 
            opt.price_incl_vat < min.price_incl_vat ? opt : min
          );
          setSelectedShipping(cheapest);
        }
      }
    } catch (err) {
      console.error("Shipping calculation error:", err);
      // Fallback to default shipping
      setShippingOptions([]);
      setSelectedShipping(null);
    } finally {
      setShippingLoading(false);
    }
  };

  // Postal code lookup
  const lookupPostalCode = async (postalCode, target = "private") => {
    const cleanPostal = postalCode.replace(/\s/g, "");
    if (cleanPostal.length !== 4) return;
    
    setPostalLookupLoading(true);
    
    try {
      const res = await axios.get(`${API}/lookup/postal/${cleanPostal}`);
      if (res.data.found) {
        if (target === "private") {
          setPrivateInfo(prev => ({ ...prev, city: res.data.city }));
        } else if (target === "company") {
          setCompanyInfo(prev => ({ ...prev, city: res.data.city }));
        } else {
          setGuestInfo(prev => ({ ...prev, city: res.data.city }));
        }
        // Also calculate shipping options
        calculateShipping(cleanPostal);
      }
    } catch (err) {
      console.error("Postal lookup error:", err);
    } finally {
      setPostalLookupLoading(false);
    }
  };

  // Select address from suggestions
  const selectAddress = (suggestion, target = "private") => {
    if (target === "private") {
      setPrivateInfo(prev => ({
        ...prev,
        address: `${suggestion.street} ${suggestion.house_number}${suggestion.letter || ""}`,
        postal_code: suggestion.postal_code,
        city: suggestion.city
      }));
    } else if (target === "company") {
      setCompanyInfo(prev => ({
        ...prev,
        address: `${suggestion.street} ${suggestion.house_number}${suggestion.letter || ""}`,
        postal_code: suggestion.postal_code,
        city: suggestion.city
      }));
    } else {
      setGuestInfo(prev => ({
        ...prev,
        address: `${suggestion.street} ${suggestion.house_number}${suggestion.letter || ""}`,
        postal_code: suggestion.postal_code,
        city: suggestion.city
      }));
    }
    setShowAddressSuggestions(false);
    setAddressQuery("");
    setAddressSuggestions([]);
  };

  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  // Calculate VAT amount (prices include 25% VAT, so VAT = total * 0.25 / 1.25 = total * 0.2)
  const vatAmount = subtotal * 0.2; // 25% VAT included in price
  const subtotalExVat = subtotal - vatAmount;
  // Use selected shipping price or default (free over 500 NOK)
  const shipping = selectedShipping ? selectedShipping.price_incl_vat : (subtotal > 500 ? 0 : 79);
  const shippingVat = shipping * 0.2; // Shipping also has 25% VAT
  const totalVat = vatAmount + shippingVat;
  const total = subtotal + shipping;

  const handleCheckout = async () => {
    // Validate based on payment method and customer type
    if (paymentMethod === "invoice") {
      if (companyInfo.customer_type === "private") {
        // Private person invoice - validate address fields
        if (!privateInfo.name || !privateInfo.email || !privateInfo.phone || !privateInfo.address || !privateInfo.postal_code || !privateInfo.city) {
          toast.error("Please fill in all required fields for private invoice");
          return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(privateInfo.email)) {
          toast.error("Email address is not valid");
          return;
        }
      } else {
        // Company invoice
        if (!companyInfo.company_name || !companyInfo.org_number) {
          toast.error("Please fill in company name and organization number");
          return;
        }
        if (!companyInfo.contact_person || !companyInfo.contact_email) {
          toast.error("Please fill in contact person name and email");
          return;
        }
      }
    } else {
      // Card payment - validate guest info if not logged in
      if (!user) {
        if (!guestInfo.name || !guestInfo.email || !guestInfo.phone || !guestInfo.address || !guestInfo.postal_code || !guestInfo.city) {
          toast.error("Please fill in all required fields including postal code and city");
          return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(guestInfo.email)) {
          toast.error("Email address is not valid");
          return;
        }
      }
    }
    
    // Validate shipping selection
    if (!selectedShipping && shippingOptions.length > 0) {
      toast.error("Please select a delivery option");
      return;
    }

    if (cart.length === 0) {
      toast.error("Cart is empty");
      return;
    }

    setLoading(true);
    try {
      const originUrl = window.location.origin;
      
      const orderData = {
        items: cart.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          size: item.size
        })),
        origin_url: originUrl,
        guest_info: !user && paymentMethod !== "invoice" ? guestInfo : null,
        company_info: paymentMethod === "invoice" && companyInfo.customer_type !== "private" ? {
          ...companyInfo
        } : null,
        private_info: paymentMethod === "invoice" && companyInfo.customer_type === "private" ? privateInfo : null,
        selected_shipping: selectedShipping
      };

      if (paymentMethod === "invoice") {
        const res = await axios.post(`${API}/shop/checkout/invoice`, orderData);
        saveCart([]);
        toast.success(`Order ${res.data.order_number} created! Invoice will be sent to your email.`);
        navigate(`/checkout/success?order_id=${res.data.order_id}&payment_method=invoice`);
      } else {
        const res = await axios.post(`${API}/shop/checkout`, orderData);
        if (res.data.checkout_url) {
          window.location.href = res.data.checkout_url;
        }
      }
    } catch (err) {
      console.error("Checkout error:", err);
      toast.error(err.response?.data?.detail || "Checkout error");
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-24 h-24 mx-auto mb-6 bg-white/5 rounded-full flex items-center justify-center">
            <ShoppingCart className="w-12 h-12 text-[#475569]" />
          </div>
          <h2 className="text-2xl font-['Unbounded'] font-bold text-white mb-2">Your cart is empty</h2>
          <p className="text-[#94A3B8] mb-6">Add products to continue</p>
          <Link to="/webshop">
            <Button className="bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 gap-2">
              <ShoppingBag className="w-4 h-4" />
              Go to Shop
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#050505]/95 backdrop-blur border-b border-white/10 pt-[env(safe-area-inset-top,0px)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/webshop" className="flex items-center gap-2 text-[#94A3B8] hover:text-white transition-colors">
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline">Back to Shop</span>
              </Link>
            </div>
            <div className="flex items-center gap-3">
              <ShoppingCart className="w-6 h-6 text-green-400" />
              <h1 className="text-xl font-['Unbounded'] font-bold text-white">Your Cart</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">{cart.length} items</h2>
              <Button 
                variant="ghost" 
                onClick={clearCart}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear Cart
              </Button>
            </div>

            {/* Cart Items - Grouped by Seller */}
            {(() => {
              // Group cart items by seller
              const sellerGroups = cart.reduce((groups, item, index) => {
                const sellerId = item.seller_id || 'allstillas';
                if (!groups[sellerId]) {
                  groups[sellerId] = {
                    seller_id: sellerId,
                    seller_name: item.seller_name || 'AllStillas',
                    seller_code: item.seller_code,
                    items: []
                  };
                }
                groups[sellerId].items.push({ ...item, originalIndex: index });
                return groups;
              }, {});
              
              const sellerGroupsArray = Object.values(sellerGroups);
              
              return sellerGroupsArray.map((group, groupIndex) => (
                <div key={group.seller_id} className="space-y-3">
                  {/* Seller Header */}
                  {sellerGroupsArray.length > 1 && (
                    <div className="flex items-center gap-3 py-2 px-3 bg-white/5 rounded-lg">
                      <div className="w-8 h-8 bg-[#F7931A]/20 rounded-lg flex items-center justify-center">
                        <Package className="w-4 h-4 text-[#F7931A]" />
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-medium text-sm">{group.seller_name}</p>
                        {group.seller_code && (
                          <p className="text-[#94A3B8] text-xs font-mono">{group.seller_code}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-[#23F7DD] font-medium">
                          {group.items.reduce((sum, item) => sum + (item.price * item.quantity), 0).toLocaleString()} NOK
                        </p>
                        <p className="text-[#94A3B8] text-xs">{group.items.length} produkt{group.items.length > 1 ? 'er' : ''}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Items from this seller */}
                  {group.items.map((item) => (
                    <Card 
                      key={`${item.product_id}-${item.size}-${item.originalIndex}`} 
                      className="bg-[#0A0A0A] border-white/10"
                      data-testid={`cart-item-${item.product_id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex gap-4">
                          <div className="w-24 h-24 bg-black/50 rounded-lg overflow-hidden flex-shrink-0">
                            {item.photo ? (
                              <img src={item.photo} alt={item.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Package className="w-8 h-8 text-[#475569]" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-white font-medium truncate">{item.name}</h3>
                            {item.size && (
                              <Badge variant="outline" className="mt-1 border-white/20 text-[#94A3B8]">
                                Size: {item.size}
                              </Badge>
                            )}
                            <p className="text-green-400 font-bold mt-2">{item.price} NOK</p>
                          </div>
                          <div className="flex flex-col items-end justify-between">
                            <button
                              onClick={() => removeItem(item.originalIndex)}
                              className="text-red-400 hover:text-red-300 p-1"
                              data-testid={`remove-item-${item.originalIndex}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                      <div className="flex items-center gap-2 bg-black/50 rounded-lg p-1">
                        <button
                          onClick={() => updateQuantity(item.originalIndex, -1)}
                          className="w-8 h-8 flex items-center justify-center text-white hover:bg-white/10 rounded"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center text-white font-medium">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.originalIndex, 1)}
                          className="w-8 h-8 flex items-center justify-center text-white hover:bg-white/10 rounded"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ))
              })()}

            {/* Payment Method Selection */}
            <Card className="bg-[#0A0A0A] border-white/10 mt-6">
              <CardHeader>
                <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-[#23F7DD]" />
                  Payment Method
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  {/* Card Payment Option */}
                  <div
                    onClick={() => setPaymentMethod("card")}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      paymentMethod === "card"
                        ? "border-[#23F7DD] bg-[#23F7DD]/10"
                        : "border-white/10 hover:border-white/30"
                    }`}
                    data-testid="payment-card"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        paymentMethod === "card" ? "border-[#23F7DD]" : "border-white/30"
                      }`}>
                        {paymentMethod === "card" && <div className="w-2.5 h-2.5 rounded-full bg-[#23F7DD]" />}
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-medium">Pay with Card</p>
                        <p className="text-xs text-[#94A3B8]">Secure payment via Stripe</p>
                      </div>
                      <CreditCard className="w-6 h-6 text-[#94A3B8]" />
                    </div>
                  </div>

                  {/* Invoice Payment Option */}
                  <div
                    onClick={() => setPaymentMethod("invoice")}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      paymentMethod === "invoice"
                        ? "border-blue-500 bg-blue-500/10"
                        : "border-white/10 hover:border-white/30"
                    }`}
                    data-testid="payment-invoice"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        paymentMethod === "invoice" ? "border-blue-500" : "border-white/30"
                      }`}>
                        {paymentMethod === "invoice" && <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />}
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-medium">Pay by Invoice</p>
                        <p className="text-xs text-[#94A3B8]">14 days payment term • For companies & private customers</p>
                      </div>
                      <FileText className="w-6 h-6 text-[#94A3B8]" />
                    </div>
                  </div>
                </div>

                {/* Card Payment - Shipping Details */}
                {paymentMethod === "card" && (
                  <div className="space-y-4 pt-4 border-t border-white/10">
                    <p className="text-sm text-[#23F7DD] font-medium">Shipping Details</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-[#94A3B8] mb-1 block">Full Name *</label>
                        <Input
                          value={guestInfo.name}
                          onChange={(e) => setGuestInfo(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="John Doe"
                          className="bg-black/50 border-white/10 text-white"
                          data-testid="guest-name"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-[#94A3B8] mb-1 block">Email *</label>
                        <Input
                          type="email"
                          value={guestInfo.email}
                          onChange={(e) => setGuestInfo(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="email@example.com"
                          className="bg-black/50 border-white/10 text-white"
                          data-testid="guest-email"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-[#94A3B8] mb-1 block">Phone *</label>
                        <Input
                          value={guestInfo.phone}
                          onChange={(e) => setGuestInfo(prev => ({ ...prev, phone: e.target.value }))}
                          placeholder="+47 XXX XX XXX"
                          className="bg-black/50 border-white/10 text-white"
                          data-testid="guest-phone"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-[#94A3B8] mb-1 block">Postal Code *</label>
                        <div className="relative">
                          <Input
                            value={guestInfo.postal_code}
                            onChange={(e) => {
                              setGuestInfo(prev => ({ ...prev, postal_code: e.target.value }));
                              if (e.target.value.length === 4) {
                                lookupPostalCode(e.target.value, "guest");
                              }
                            }}
                            placeholder="0001"
                            className="bg-black/50 border-white/10 text-white"
                            data-testid="guest-postal"
                          />
                          {postalLookupLoading && (
                            <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-[#23F7DD] animate-spin" />
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Address with Autocomplete */}
                    <div className="relative">
                      <label className="text-sm text-[#94A3B8] mb-1 block">Address *</label>
                      <div className="relative">
                        <Input
                          value={guestInfo.address || addressQuery}
                          onChange={(e) => {
                            setAddressQuery(e.target.value);
                            setGuestInfo(prev => ({ ...prev, address: e.target.value }));
                            setShowAddressSuggestions(true);
                          }}
                          onFocus={() => setShowAddressSuggestions(addressSuggestions.length > 0)}
                          placeholder="Start typing to search..."
                          className="bg-black/50 border-white/10 text-white"
                          data-testid="guest-address"
                        />
                        <MapPin className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                      </div>
                      
                      {/* Address Suggestions Dropdown */}
                      {showAddressSuggestions && addressSuggestions.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-[#0A0A0A] border border-white/10 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                          {addressSuggestions.map((suggestion, idx) => (
                            <div
                              key={idx}
                              onClick={() => selectAddress(suggestion, "guest")}
                              className="px-4 py-3 hover:bg-white/5 cursor-pointer border-b border-white/5 last:border-0"
                            >
                              <p className="text-white text-sm">{suggestion.full_address}</p>
                              <p className="text-[#64748B] text-xs">{suggestion.postal_code} {suggestion.city}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <label className="text-sm text-[#94A3B8] mb-1 block">City *</label>
                      <Input
                        value={guestInfo.city}
                        onChange={(e) => setGuestInfo(prev => ({ ...prev, city: e.target.value }))}
                        placeholder="Auto-filled from postal code"
                        className="bg-black/50 border-white/10 text-white"
                        data-testid="guest-city"
                      />
                    </div>
                    
                    <div className="p-3 bg-[#23F7DD]/10 rounded-lg border border-[#23F7DD]/30">
                      <p className="text-xs text-[#23F7DD]">
                        🔒 Card payment is secured by Stripe. You'll enter card details on the next page.
                      </p>
                    </div>
                  </div>
                )}

                {/* Invoice Details */}
                {paymentMethod === "invoice" && (
                  <div className="space-y-4 pt-4 border-t border-white/10">
                    <p className="text-sm text-blue-400 font-medium">Invoice Details</p>
                    
                    {/* Customer Type Toggle */}
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={companyInfo.customer_type !== "private" ? "default" : "outline"}
                        onClick={() => setCompanyInfo(prev => ({ ...prev, customer_type: "company" }))}
                        className={companyInfo.customer_type !== "private" 
                          ? "bg-blue-500 text-white flex-1" 
                          : "border-white/20 text-white flex-1"}
                        size="sm"
                      >
                        <Building2 className="w-4 h-4 mr-1" />
                        Company (B2B)
                      </Button>
                      <Button
                        type="button"
                        variant={companyInfo.customer_type === "private" ? "default" : "outline"}
                        onClick={() => setCompanyInfo(prev => ({ ...prev, customer_type: "private" }))}
                        className={companyInfo.customer_type === "private" 
                          ? "bg-blue-500 text-white flex-1" 
                          : "border-white/20 text-white flex-1"}
                        size="sm"
                      >
                        <User className="w-4 h-4 mr-1" />
                        Private Person
                      </Button>
                    </div>

                    {/* Company Fields (B2B) */}
                    {companyInfo.customer_type !== "private" && (
                      <div className="space-y-4">
                        {/* Org Number with Lookup */}
                        <div>
                          <label className="text-sm text-[#94A3B8] mb-1 block">Organization Number *</label>
                          <div className="flex gap-2">
                            <div className="flex-1 relative">
                              <Input
                                value={companyInfo.org_number}
                                onChange={(e) => setCompanyInfo(prev => ({ ...prev, org_number: e.target.value }))}
                                onBlur={(e) => lookupCompany(e.target.value)}
                                placeholder="123 456 789"
                                className="bg-black/50 border-white/10 text-white pr-10"
                                data-testid="org-number"
                              />
                              {companyLookupLoading && (
                                <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-blue-400 animate-spin" />
                              )}
                              {companyFound && !companyLookupLoading && (
                                <Check className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-green-400" />
                              )}
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => lookupCompany(companyInfo.org_number)}
                              disabled={companyLookupLoading}
                              className="border-white/20 text-white"
                            >
                              <Search className="w-4 h-4" />
                            </Button>
                          </div>
                          <p className="text-xs text-[#64748B] mt-1">Enter org number to auto-fill company details from Brønnøysund</p>
                        </div>

                        <div>
                          <label className="text-sm text-[#94A3B8] mb-1 block">Company Name *</label>
                          <Input
                            value={companyInfo.company_name}
                            onChange={(e) => setCompanyInfo(prev => ({ ...prev, company_name: e.target.value }))}
                            placeholder="DOSERNE AS"
                            className="bg-black/50 border-white/10 text-white"
                            data-testid="company-name"
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm text-[#94A3B8] mb-1 block">Address</label>
                            <Input
                              value={companyInfo.address}
                              onChange={(e) => setCompanyInfo(prev => ({ ...prev, address: e.target.value }))}
                              placeholder="Street address"
                              className="bg-black/50 border-white/10 text-white"
                            />
                          </div>
                          <div>
                            <label className="text-sm text-[#94A3B8] mb-1 block">Postal Code</label>
                            <Input
                              value={companyInfo.postal_code}
                              onChange={(e) => {
                                setCompanyInfo(prev => ({ ...prev, postal_code: e.target.value }));
                                if (e.target.value.length === 4) {
                                  lookupPostalCode(e.target.value, "company");
                                }
                              }}
                              placeholder="0001"
                              className="bg-black/50 border-white/10 text-white"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="text-sm text-[#94A3B8] mb-1 block">City</label>
                          <Input
                            value={companyInfo.city}
                            onChange={(e) => setCompanyInfo(prev => ({ ...prev, city: e.target.value }))}
                            placeholder="Oslo"
                            className="bg-black/50 border-white/10 text-white"
                          />
                        </div>

                        {/* Contact Person */}
                        <div className="pt-3 border-t border-white/5">
                          <p className="text-sm text-blue-400 font-medium mb-3">Contact Person</p>
                          <div className="space-y-3">
                            <div>
                              <label className="text-sm text-[#94A3B8] mb-1 block">Contact Name *</label>
                              <Input
                                value={companyInfo.contact_person}
                                onChange={(e) => setCompanyInfo(prev => ({ ...prev, contact_person: e.target.value }))}
                                placeholder="John Doe"
                                className="bg-black/50 border-white/10 text-white"
                                data-testid="contact-person"
                              />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="text-sm text-[#94A3B8] mb-1 block">Contact Email *</label>
                                <Input
                                  type="email"
                                  value={companyInfo.contact_email}
                                  onChange={(e) => setCompanyInfo(prev => ({ ...prev, contact_email: e.target.value }))}
                                  placeholder="contact@company.no"
                                  className="bg-black/50 border-white/10 text-white"
                                  data-testid="contact-email"
                                />
                              </div>
                              <div>
                                <label className="text-sm text-[#94A3B8] mb-1 block">Contact Phone</label>
                                <Input
                                  value={companyInfo.contact_phone}
                                  onChange={(e) => setCompanyInfo(prev => ({ ...prev, contact_phone: e.target.value }))}
                                  placeholder="+47 XXX XX XXX"
                                  className="bg-black/50 border-white/10 text-white"
                                  data-testid="contact-phone"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Private Person Fields */}
                    {companyInfo.customer_type === "private" && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm text-[#94A3B8] mb-1 block">Full Name *</label>
                            <Input
                              value={privateInfo.name}
                              onChange={(e) => setPrivateInfo(prev => ({ ...prev, name: e.target.value }))}
                              placeholder="John Doe"
                              className="bg-black/50 border-white/10 text-white"
                              data-testid="private-name"
                            />
                          </div>
                          <div>
                            <label className="text-sm text-[#94A3B8] mb-1 block">Email *</label>
                            <Input
                              type="email"
                              value={privateInfo.email}
                              onChange={(e) => setPrivateInfo(prev => ({ ...prev, email: e.target.value }))}
                              placeholder="email@example.com"
                              className="bg-black/50 border-white/10 text-white"
                              data-testid="private-email"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="text-sm text-[#94A3B8] mb-1 block">Phone *</label>
                          <Input
                            value={privateInfo.phone}
                            onChange={(e) => setPrivateInfo(prev => ({ ...prev, phone: e.target.value }))}
                            placeholder="+47 XXX XX XXX"
                            className="bg-black/50 border-white/10 text-white"
                            data-testid="private-phone"
                          />
                        </div>

                        {/* Address with Autocomplete */}
                        <div className="relative">
                          <label className="text-sm text-[#94A3B8] mb-1 block">Address *</label>
                          <div className="relative">
                            <Input
                              ref={addressInputRef}
                              value={privateInfo.address || addressQuery}
                              onChange={(e) => {
                                setAddressQuery(e.target.value);
                                setPrivateInfo(prev => ({ ...prev, address: e.target.value }));
                                setShowAddressSuggestions(true);
                              }}
                              onFocus={() => setShowAddressSuggestions(addressSuggestions.length > 0)}
                              placeholder="Start typing to search..."
                              className="bg-black/50 border-white/10 text-white"
                              data-testid="private-address"
                            />
                            <MapPin className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                          </div>
                          
                          {/* Address Suggestions Dropdown */}
                          {showAddressSuggestions && addressSuggestions.length > 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-[#0A0A0A] border border-white/10 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                              {addressSuggestions.map((suggestion, idx) => (
                                <div
                                  key={idx}
                                  onClick={() => selectAddress(suggestion, "private")}
                                  className="px-4 py-3 hover:bg-white/5 cursor-pointer border-b border-white/5 last:border-0"
                                >
                                  <p className="text-white text-sm">{suggestion.full_address}</p>
                                  <p className="text-[#64748B] text-xs">{suggestion.postal_code} {suggestion.city}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm text-[#94A3B8] mb-1 block">Postal Code *</label>
                            <div className="relative">
                              <Input
                                value={privateInfo.postal_code}
                                onChange={(e) => {
                                  setPrivateInfo(prev => ({ ...prev, postal_code: e.target.value }));
                                  if (e.target.value.length === 4) {
                                    lookupPostalCode(e.target.value, "private");
                                  }
                                }}
                                placeholder="0001"
                                className="bg-black/50 border-white/10 text-white"
                                data-testid="private-postal"
                              />
                              {postalLookupLoading && (
                                <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-blue-400 animate-spin" />
                              )}
                            </div>
                          </div>
                          <div>
                            <label className="text-sm text-[#94A3B8] mb-1 block">City *</label>
                            <Input
                              value={privateInfo.city}
                              onChange={(e) => setPrivateInfo(prev => ({ ...prev, city: e.target.value }))}
                              placeholder="Auto-filled from postal code"
                              className="bg-black/50 border-white/10 text-white"
                              data-testid="private-city"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
                      <p className="text-xs text-blue-400">
                        📧 Invoice will be sent to {companyInfo.customer_type === "private" ? "your email" : `${companyInfo.contact_email || "contact email"}`} within 24 hours.<br/>
                        💳 Payment due within 14 days from order date.
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="bg-[#0A0A0A] border-white/10 sticky top-24">
              <CardHeader>
                <CardTitle className="text-white font-['Unbounded']">Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  {/* Subtotal ex VAT */}
                  <div className="flex justify-between text-[#94A3B8]">
                    <span>Subtotal (ex. VAT)</span>
                    <span>{subtotalExVat.toLocaleString('nb-NO', { maximumFractionDigits: 0 })} NOK</span>
                  </div>
                  
                  {/* VAT Amount */}
                  <div className="flex justify-between text-[#94A3B8]">
                    <span>VAT (25%)</span>
                    <span>{vatAmount.toLocaleString('nb-NO', { maximumFractionDigits: 0 })} NOK</span>
                  </div>
                  
                  {/* Subtotal inc VAT */}
                  <div className="flex justify-between text-white font-medium">
                    <span>Subtotal (inc. VAT)</span>
                    <span>{subtotal.toLocaleString('nb-NO', { maximumFractionDigits: 0 })} NOK</span>
                  </div>
                  
                  {/* Shipping Options - always show */}
                  <div className="py-2">
                    <div className="flex justify-between items-center text-[#94A3B8] mb-2">
                      <span>Delivery</span>
                      {shippingLoading && <Loader2 className="w-4 h-4 animate-spin text-blue-400" />}
                    </div>
                    
                    {shippingOptions.length > 0 ? (
                      <div className="space-y-2">
                        {shippingOptions.map((option, idx) => (
                          <div
                            key={option.id || idx}
                            onClick={() => setSelectedShipping(option)}
                            className={`p-3 rounded-lg border cursor-pointer transition-all ${
                              selectedShipping?.id === option.id
                                ? option.is_free ? "border-green-500 bg-green-500/10" : "border-blue-500 bg-blue-500/10"
                                : "border-white/10 hover:border-white/20"
                              }`}
                            >
                              <div className="flex justify-between items-start">
                                <div className="flex items-start gap-2">
                                  <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center ${
                                    selectedShipping?.id === option.id
                                      ? option.is_free ? "border-green-500" : "border-blue-500"
                                      : "border-white/30"
                                  }`}>
                                    {selectedShipping?.id === option.id && (
                                      <div className={`w-2 h-2 rounded-full ${option.is_free ? "bg-green-500" : "bg-blue-500"}`} />
                                    )}
                                  </div>
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <p className="text-white text-sm font-medium">{option.service_name}</p>
                                      {option.is_free && (
                                        <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">FREE</span>
                                      )}
                                    </div>
                                    <p className="text-[#64748B] text-xs">{option.carrier} • {option.estimated_days}</p>
                                    {option.description && (
                                      <p className="text-[#475569] text-xs mt-1">{option.description}</p>
                                    )}
                                  </div>
                                </div>
                                <div className="text-right">
                                  {option.is_free ? (
                                    <p className="text-green-400 font-bold">FREE</p>
                                  ) : (
                                    <p className="text-white font-medium">{option.price_incl_vat} NOK</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex justify-between text-[#94A3B8]">
                          <span className="text-xs">Standard shipping</span>
                          <span>{shipping} NOK</span>
                        </div>
                      )}
                      
                      {!shippingOptions.length && (
                        <p className="text-xs text-[#64748B] mt-2">
                          Enter postal code for delivery options
                        </p>
                      )}
                    </div>
                  
                  <div className="border-t border-white/10 pt-2 space-y-2">
                    {/* Total VAT */}
                    <div className="flex justify-between text-[#64748B] text-sm">
                      <span>Total VAT (25%)</span>
                      <span>{totalVat.toLocaleString('nb-NO', { maximumFractionDigits: 0 })} NOK</span>
                    </div>
                    {/* Total */}
                    <div className="flex justify-between text-lg font-bold">
                      <span className="text-white">Total (inc. VAT)</span>
                      <span className="text-green-400">{total.toLocaleString('nb-NO', { maximumFractionDigits: 0 })} NOK</span>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleCheckout}
                  disabled={loading}
                  className={`w-full py-6 text-lg ${
                    paymentMethod === "invoice"
                      ? "bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
                      : "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                  } text-white`}
                  data-testid="checkout-button"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : paymentMethod === "invoice" ? (
                    <>
                      <FileText className="w-5 h-5 mr-2" />
                      Place Order (Invoice)
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Proceed to Payment
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>

                {paymentMethod === "invoice" && (
                  <p className="text-xs text-blue-400 text-center">
                    You will receive an invoice by email with 14 days payment term
                  </p>
                )}

                {/* Trust badges */}
                <div className="grid grid-cols-3 gap-2 pt-4 border-t border-white/10">
                  <div className="text-center">
                    <Shield className="w-6 h-6 mx-auto text-green-400 mb-1" />
                    <span className="text-xs text-[#94A3B8]">Secure Payment</span>
                  </div>
                  <div className="text-center">
                    <Truck className="w-6 h-6 mx-auto text-green-400 mb-1" />
                    <span className="text-xs text-[#94A3B8]">Fast Delivery</span>
                  </div>
                  <div className="text-center">
                    <Sparkles className="w-6 h-6 mx-auto text-green-400 mb-1" />
                    <span className="text-xs text-[#94A3B8]">Quality Guarantee</span>
                  </div>
                </div>

                {user && (
                  <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                    <div className="flex items-center gap-2 text-green-400 text-sm">
                      <User className="w-4 h-4" />
                      <span>Logged in as {user.name || user.email}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-4 mt-12">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="px-2 py-1 bg-[#0A0A0A] border border-[#23F7DD]/50 rounded">
              <span className="text-[#23F7DD] font-bold text-sm font-['Unbounded']">All</span>
            </div>
            <span className="text-sm font-bold font-['Unbounded'] text-white">Stillas</span>
            <div className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-[#23F7DD]/10 border border-[#23F7DD]/20 rounded-full">
              <span className="text-[#23F7DD] text-[10px]">⚡</span>
              <span className="text-[10px] text-[#94A3B8]">Powered by</span>
              <span className="text-[10px] text-[#23F7DD]">Doserne AS</span>
            </div>
          </Link>
          <p className="text-sm text-[#64748B]">© 2026 Doserne AS. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
